import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { surveys } from '@/api/surveys';
import type { SurveyRow, CreateSurveyBody, SurveyKind } from '@/api/surveys';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { PageHeader } from '@/components/PageHeader';
import { formatRef } from '@/lib/formatRef';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Plus, MoreHorizontal, Trash2, ClipboardList, Search, MessageSquareText, Mic, Pencil,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

export function SurveysList() {
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const canMutate = CAN_MUTATE.includes(user.role);
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SurveyRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['surveys', search, effectiveCustomerId],
    queryFn: () => surveys.list({
      search: search.trim() || undefined,
      limit: 200,
      customer_id: effectiveCustomerId ?? undefined,
    }),
  });
  const rows = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => surveys.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['surveys'] }); setDeleteTarget(null); },
  });

  return (
    <>
      <PageHeader
        eyebrow="Education"
        title="Surveys"
        description="Voice + SMS questionnaires. Callers answer by DTMF or text; answers land in the response ledger."
        actions={canMutate && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            New survey
          </Button>
        )}
      />

      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
        <Input
          placeholder="Search surveys…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState onCreate={canMutate ? () => setCreateOpen(true) : undefined} />
      ) : (
        <div className="rounded-lg border border-border bg-white overflow-hidden">
          <ul className="divide-y divide-border">
            {rows.map((s, idx) => (
              <SurveyRowItem
                key={s.id}
                index={idx + 1}
                row={s}
                canMutate={canMutate}
                onDelete={() => setDeleteTarget(s)}
              />
            ))}
          </ul>
        </div>
      )}

      {canMutate && (
        <CreateSurveyDialog open={createOpen} onOpenChange={setCreateOpen} />
      )}

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete survey?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{deleteTarget?.name}</span> will be soft-deleted.
            Completed responses stay in the audit ledger.
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" size="sm">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive" size="sm"
              disabled={remove.isPending}
              onClick={() => deleteTarget && remove.mutate(deleteTarget.id)}
            >
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EmptyState({ onCreate }: { onCreate?: () => void }) {
  return (
    <Card>
      <CardContent className="py-16 text-center">
        <ClipboardList className="mx-auto h-8 w-8 text-slate-300 mb-3" />
        <p className="text-sm font-medium text-slate-500">No surveys yet</p>
        <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
          Create a survey, add questions, then point an IVR{' '}
          <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">survey</code>{' '}
          node at it. Callers who reach that node walk the questions.
        </p>
        {onCreate && (
          <Button size="sm" className="mt-4" onClick={onCreate}>
            <Plus className="h-4 w-4 mr-1.5" />
            Create your first survey
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function SurveyRowItem({
  index, row, canMutate, onDelete,
}: {
  index: number;
  row: SurveyRow;
  canMutate: boolean;
  onDelete: () => void;
}) {
  const navigate = useNavigate();
  // Compute a rough completion rate — if started > 0, use the ratio
  // but we only have completed count + "started" isn't broken out,
  // so fall back to showing raw completed count.
  const completed = row.response_count;
  // Bar width = log-scaled completed count (visual only)
  const barWidth = Math.min(100, Math.round(Math.sqrt(completed) * 10));

  return (
    <li className="group relative">
      <Link
        to={`/surveys/SRV-${row.ref_number}`}
        className="grid grid-cols-[3rem_minmax(0,1fr)_8rem_9rem_6rem_2rem] items-center gap-4 px-5 py-4 hover:bg-slate-50/60 transition-colors"
      >
        {/* Index number — editorial flourish */}
        <span className="text-lg font-mono font-bold text-slate-300 tabular-nums leading-none">
          {String(index).padStart(2, '0')}
        </span>

        {/* Name + ref + description */}
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={cn(
              'text-[10px] font-mono uppercase tracking-wider',
              row.active ? 'text-emerald-700' : 'text-slate-400',
            )}>
              {formatRef(row.ref_number, 'SRV')}
            </span>
            {!row.active && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Inactive
              </span>
            )}
          </div>
          <div className="text-sm font-semibold text-foreground truncate">
            {row.name}
          </div>
          {row.description && (
            <div className="text-xs text-muted-foreground truncate mt-0.5">
              {row.description}
            </div>
          )}
        </div>

        {/* Kind chip */}
        <KindBadge kind={row.kind} />

        {/* Questions + points */}
        <div className="flex flex-col gap-0.5 text-[11px] text-muted-foreground">
          <span>
            <span className="font-mono tabular-nums font-semibold text-foreground">{row.question_count}</span>{' '}
            question{row.question_count === 1 ? '' : 's'}
          </span>
          <span>
            <span className="font-mono tabular-nums font-semibold text-amber-700">
              +{row.points_on_complete}
            </span>{' '}
            pts on complete
          </span>
        </div>

        {/* Responses with bar */}
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">
            <span className="font-mono tabular-nums font-semibold text-foreground">
              {completed}
            </span>{' '}
            response{completed === 1 ? '' : 's'}
          </span>
          <div className="relative h-1 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-primary/80 transition-all"
              style={{ width: `${barWidth}%` }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end">
          {canMutate && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon" variant="ghost"
                  className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => e.preventDefault()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); navigate(`/surveys/${row.id}`); }}
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  Edit survey
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); onDelete(); }}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Delete survey
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </Link>
    </li>
  );
}

function KindBadge({ kind }: { kind: SurveyKind }) {
  if (kind === 'voice') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-sky-700 ring-1 ring-sky-200 w-fit">
        <Mic className="h-2.5 w-2.5" />
        Voice
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-700 ring-1 ring-violet-200 w-fit">
      <MessageSquareText className="h-2.5 w-2.5" />
      SMS
    </span>
  );
}

function CreateSurveyDialog({
  open, onOpenChange,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<SurveyKind>('voice');
  const [points, setPoints] = useState('0');
  const [error, setError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const { effectiveCustomerId, element: customerPicker, isPickerVisible } =
    useCustomerPicker(customerId, setCustomerId, { required: true });

  const create = useMutation({
    mutationFn: (body: CreateSurveyBody) => surveys.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surveys'] });
      setName(''); setDescription(''); setKind('voice'); setPoints('0');
      setCustomerId(null); setError(null);
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Create failed.'),
  });

  const pts = Number(points);
  const validPts = Number.isFinite(pts) && pts >= 0 && Number.isInteger(pts);
  const canSubmit = !!name.trim() && validPts && effectiveCustomerId !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New survey</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {customerPicker}
          <CustomerScopeChip />
          <div className="space-y-1">
            <Label htmlFor="srv-name">Name</Label>
            <Input
              id="srv-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Post-class feedback"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="srv-desc">Description (optional)</Label>
            <Input
              id="srv-desc" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="What this survey is for"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Kind</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as SurveyKind)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="voice">Voice (IVR)</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="srv-pts">Points on complete</Label>
              <Input
                id="srv-pts" type="number" min="0" step="1"
                value={points} onChange={(e) => setPoints(e.target.value)}
                className="font-mono"
              />
            </div>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={create.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            disabled={create.isPending || !canSubmit}
            onClick={() => create.mutate({
              name: name.trim(),
              description: description.trim() || null,
              kind,
              points_on_complete: pts,
              ...(effectiveCustomerId
                ? { customer_id: effectiveCustomerId }
                : {}),
            })}
          >
            {create.isPending ? 'Creating…' : 'Create survey'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
